import React, { useEffect, useState } from 'react'
import { matchRoutes } from 'react-router-config'
import Helmet from 'react-helmet'
import { OneModal as Modal } from '@yy/one-ui'
import ProLayout from '@ant-design/pro-layout'
import { useHistory } from 'react-router-dom'
import { antRoutes, shutdownIntercom, updateIntercomUserInfo, insertIntercomWithLogin } from '@/utils/'
import { useCheckAuth, useSocket, usePC } from '@/hooks'
import { useDispatch } from '@/store'
import { getAllEnums, setEnums } from '@/store/commonSlice'
import { RouterGuard } from '@/components/Authorization'
import { ErrorBoundary } from './components/'
import { BreadcrumbContext } from './context'
import styles from './PrimaryLayout.module.less'
import { useTranslation } from 'react-i18next'
import Logo from '@/static/imgs/logo.svg'
import classNames from 'classnames'
import { setShouldRefreshCache } from '@/store/cacheSlice'
import { useSelector } from '@/store'
import { AliveScope, useAliveController } from 'react-activation'
import { useMount, useUnmount } from 'ahooks'
import { slTraceInit } from '@/utils/slTrace'
import BellNotice from '@/containers/BellCenter/components/BellNotice'
import { splitSupportCountryMapCreator } from '@/store/addressSlice'
import { getWalletInfo } from '@/store/authSlice'
import { ChevronDown, ChevronUp } from '@yy/one-icon'
import useImportConfig from '@/hooks/useImportConfig'

const TheFeedback = React.lazy(() => import('@/bizComponents/TheFeedback'))
const RightContent = React.lazy(() => import('./components/RightContent'))

const PrimaryLayout = (props) => {
  const { route, ...others } = props
  const matchedRoutes = matchRoutes([route], location.pathname)
  const lang = useSelector((state) => state.language.lang)
  const { t } = useTranslation()
  const history = useHistory()
  const { checkAuth } = useCheckAuth()
  const dispatch = useDispatch()
  const { getImportConfig } = useImportConfig()
  const { isPC } = usePC()
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    // 初始化所有的枚举值
    dispatch(getAllEnums())
    dispatch(splitSupportCountryMapCreator())

    return () => {
      setEnums(null)
    }
  }, [dispatch, lang])

  // 接入socket
  useSocket()
  useMount(() => {
    dispatch(getWalletInfo())
  })

  useEffect(() => {
    if (!isPC) {
      setVisible(true)
    }
  }, [isPC])

  useMount(() => {
    // 埋点
    slTraceInit()
    if (window.Intercom) {
      updateIntercomUserInfo()
    } else {
      insertIntercomWithLogin()
    }

    // 获取导入配置
    getImportConfig()
    return () => {
      shutdownIntercom()
    }
  })

  // 过滤菜单
  const menuFilter = (routes) => {
    const filterRoutes = routes.filter((i) => {
      // 没权限直接整个父菜 单不显示
      if (!checkAuth(i.authCode)) {
        return false
      }
      if (i.children && i.children.length) {
        i.children = i.children.filter((j) => {
          return !j.hiddenInMenu && checkAuth(j.authCode)
        })
      }
      return true
    })
    return antRoutes(filterRoutes, t)
  }

  const menuHeaderRender = (logo, title, sideStatus) => {
    if (sideStatus?.collapsed || !sideStatus) {
      // 侧边栏收起时占位用
      return <div style={{ height: 25 }}></div>
    }
    return <img src={Logo}></img>
  }

  const handleSubMenuClick = (item) => {
    dispatch(setShouldRefreshCache(true))
    history.push(item?.children?.[0].path)
  }
  const handleMenuClick = (item) => {
    dispatch(setShouldRefreshCache(true))
    history.push(item.path)
  }

  // 组件卸载，清空所有缓存
  const { clear } = useAliveController()
  useUnmount(() => {
    clear && clear()
    dispatch(setShouldRefreshCache(false))
  })
  return (
    <>
      <Helmet>
        <html className={styles.fullbody} />
        <body className={styles.fullbody} />
      </Helmet>
      <AliveScope>
        <>
          <ProLayout
            logo={false}
            title="OneWarehouse"
            menuHeaderRender={menuHeaderRender}
            fixedHeader
            siderWidth={240}
            fixSiderbar
            className={styles.prolayout}
            navTheme="light"
            route={{ routes: antRoutes(route.routes, t) }}
            menuDataRender={menuFilter}
            menuItemRender={(item) =>
              item.path ? (
                <div style={{ cursor: 'pointer' }} onClick={() => handleMenuClick(item)}>
                  <span className={classNames('sub-menu-icon', styles.submenu)}>
                    <span className={classNames('anticon', 'anticon-tablet', { [styles.hasIcon]: !!item.icon })}>{item.icon}</span>
                    <span className={styles.childMenu}>{t(item.menuTitle)}</span>
                  </span>
                </div>
              ) : (
                t(item.menuTitle)
              )
            }
            menuProps={{
              expandIcon: ({ isOpen }) => {
                return isOpen ? <ChevronUp className="menu-arrow-icon" /> : <ChevronDown className="menu-arrow-icon" />
              },
            }}
            subMenuItemRender={(item) => {
              return (
                <div onClick={() => handleSubMenuClick(item)} className={classNames('sub-menu-icon', styles.submenu)}>
                  <span className="anticon anticon-tablet">{item.icon}</span>
                  <span>{t(item.menuTitle)}</span>
                </div>
              )
            }}
            rightContentRender={() => <RightContent />}
            {...others}
          >
            <ErrorBoundary>
              <BreadcrumbContext.Provider value={matchedRoutes}>
                <div id="scroll-content-target" className={styles.contentHeight}>
                  <RouterGuard {...props} />
                </div>
              </BreadcrumbContext.Provider>
            </ErrorBoundary>
            {/* </div> */}
          </ProLayout>
          <Modal
            visible={!isPC && visible}
            maskClosable={false}
            okText={t('common.confirm')}
            cancelText={t('common.cancel')}
            title={<></>}
            onCancel={() => {
              setVisible(false)
            }}
            onOk={() => {
              setVisible(false)
            }}
          >
            {t('common.noMobile')}
          </Modal>
        </>
      </AliveScope>

      <BellNotice />
      <TheFeedback />
    </>
  )
}

export default PrimaryLayout
